import { ReactNode } from 'react';
import { usePermissions } from '../../hooks/usePermissions';

type PermissionKey = keyof ReturnType<typeof usePermissions>;

interface ProtectedElementProps {
  permission?: PermissionKey;
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Component to conditionally render children based on user permissions
 * 
 * @example
 * // Single permission
 * <ProtectedElement permission="canCreateProject">
 *   <button>Crear Proyecto</button>
 * </ProtectedElement>
 * 
 * @example
 * // With fallback
 * <ProtectedElement permission="canManageUsers" fallback={<p>No tienes acceso</p>}>
 *   <UserManagement />
 * </ProtectedElement>
 */
export const ProtectedElement = ({
  permission,
  fallback = null,
  children,
}: ProtectedElementProps) => {
  const permissions = usePermissions();

  if (!permission) {
    return <>{children}</>;
  }

  const hasAccess = permissions[permission];

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
